import { Component, OnInit } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import { forkJoin } from 'rxjs'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { ClientDto } from 'src/app/models/client/client-dto'
import { Filter } from 'src/app/models/power-bi/filter'
import { ReddiHistoryTableDTO } from 'src/app/models/reddi/reddi-history-table-dto'
import { ReddiListDto } from 'src/app/models/reddi/reddi-list-dto'
import { ReddiHistoryTableColumnDefinitions } from 'src/app/models/table-defs/reddi-history-table-def'
import { TableColumnDefinition } from 'src/app/models/table/table-column-definition'
import { TableLinkColumnDTO } from 'src/app/models/table/table-link-column-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { FeaturesService } from 'src/app/services/features/features.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import {
  ImpactingFactor,
  ImpactingFactorToDisplayName,
} from 'src/app/services/reddi/reddi-utils'
import { ReddiService } from 'src/app/services/reddi/reddi.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'

@Component({
  selector: 'app-reddi-history',
  templateUrl: './reddi-history.component.html',
  styleUrls: ['./reddi-history.component.scss'],
  standalone: false,
})
export class ReddiHistoryComponent implements OnInit {
  public _clientId: string
  _title = ''
  _age: string = ''
  _dob: Date
  _dateFormat: string = 'Do MMMM, YYYY'
  _fallbackUrl = '/dashboard'
  _reddiCreateReportUrl: string

  _clientDetails: ClientDto
  _reddiHistory: ReddiListDto[]
  _reddiFilters: Filter[]
  _tableData: MatTableDataSource<ReddiHistoryTableDTO> =
    new MatTableDataSource<ReddiHistoryTableDTO>()
  _tableColumnsToDisplay: string[] = [
    'presentationDate',
    'age',
    'presenterType',
    'impactingFactors',
    'presenterName',
    'notes',
    'viewReport',
  ]
  _tableColumnDefinitions: TableColumnDefinition[] =
    ReddiHistoryTableColumnDefinitions

  //Client Attributes
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInFocus: string = ''
  _clientEditEnabled: boolean = false //Perform check based on Feature Flag
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }

  constructor(
    private route: ActivatedRoute,
    private bannerService: BannerService,
    protected spinnerService: SpinnerService,
    private clientService: ClientService,
    private reddiService: ReddiService,
    private previousRouteService: PreviousRouteService,
    public router: Router,
    private commonService: CommonService,
    public featureService: FeaturesService,
  ) {}

  ngOnInit() {
    this.spinnerService.show()

    this._clientId = this.route.snapshot.paramMap.get('clientId')
    if (!this.commonService.isValidUUID(this._clientId)) {
      this.bannerService.showBanner(`Invalid client requested.`, 'error', 3000)
      this.previousRouteService.goToPreviousUrl(this._fallbackUrl)
    }

    this._reddiFilters = [
      {
        attribute: 'Client/contactid',
        operation: 'eq',
        value: this._clientId,
      },
    ]

    this._reddiCreateReportUrl = '/reddi/reddi-report-form/' + this._clientId
    let requests = {
      clientDetails: this.clientService.Details(this._clientId),
      reddiHistory: this.reddiService.getList(this._clientId),
      features: this.featureService.GetFeatures(),
    }

    forkJoin(requests).subscribe({
      next: ({ clientDetails, reddiHistory, features }) => {
        this._clientEditEnabled = this.commonService.getFeatureValue(
          features,
          'clientEdit',
        )
        this._clientDetails = clientDetails
        this._age = this._clientDetails.age
        this._dob = this._clientDetails.dateOfBirth
        this._title = this._clientDetails.preferredFullName
        this.setClientInformation(clientDetails)
        this._reddiHistory = reddiHistory
        this._tableData = this.MapHistoryToTable(
          this._clientId,
          this._reddiHistory,
        )
        //tools has access check
        this.checkToolsAccess()
        this.retriveIndicators(clientDetails)
        this.spinnerService.hide()
      },
      error: (e) => {
        this.bannerService.showBanner(
          `Unable to retrieve details.`,
          'error',
          3000,
        )
        console.log('error: ', e)
        this.spinnerService.hide()
      },
    })
  }

  redirect(destination: string) {
    this.router.navigate([destination])
  }

  routerLinkClicked() {
    this.router.navigate([`/client-dashboard/${this._clientId}`])
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this._clientId)
  }

  MapHistoryToTable(
    clientId: string,
    history: ReddiListDto[],
  ): MatTableDataSource<ReddiHistoryTableDTO> {
    const tableData: ReddiHistoryTableDTO[] = new Array<ReddiHistoryTableDTO>()
    history.sort((a, b) => {
      // Sorting in ascending order
      return (
        moment(b.presentationDate).valueOf() -
        moment(a.presentationDate).valueOf()
      )
    })
    for (let i = 0; i < history.length; i++) {
      let source = history[i]

      let target = new ReddiHistoryTableDTO()
      target.notes = source.notes
      target.presentationDate = moment(source.presentationDate).format(
        this._dateFormat,
      )
      target.presenterName = source.presenterName
      target.presenterType = source.presenterRole
      target.age = source.ageInMonthsAtAssessmentDate
      for (let i = 0; i < source.impactingFactors.length; i++) {
        if (source.impactingFactors[i] !== ImpactingFactor.None) {
          if (target.impactingFactors === undefined) {
            target.impactingFactors = ImpactingFactorToDisplayName(
              source.impactingFactors[i],
            )
          } else {
            target.impactingFactors =
              target.impactingFactors +
              ', ' +
              ImpactingFactorToDisplayName(source.impactingFactors[i])
          }
        }
      }
      target.viewReport = new TableLinkColumnDTO()
      target.viewReport.linkText = 'View details'
      target.viewReport.linkUrl = `/reddi/instance/${clientId}/${source.reddiId}`

      tableData.push(target)
    }

    return new MatTableDataSource<ReddiHistoryTableDTO>(tableData)
  }

  setClientInformation(client: ClientDto) {
    //this._clientInformationTitle = client.preferredFullName
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._clientDetails,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._clientDetails,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._clientDetails,
    )
  }

  retriveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }
}
